import { DrawerActions, useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';
import ReactPlayer from 'react-player';

import ContactModal from './ContactModalScreen';
import { getUrlFromImageName } from '../api/images';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { CoverImage } from '../components/CoverImage';
import { CustomIcon } from '../components/CustomIcon';
import CustomText from '../components/CustomText';
import { GradientBackground } from '../components/GradientBackground';
import { LogoImage } from '../components/LogoImage';
import { MenuIcon } from '../components/MenuIcon';
import { PlayPauseButton } from '../components/PlayPauseButton';
import { ScreenLayout } from '../components/ScreenLayout';
import { ScrollableText } from '../components/ScrollableText';
import { SleepTimer } from '../components/SleepTimer';
import { VoteButtons } from '../components/VoteButtons';
import { COLORS, IS_IOS, isTablet, REDUCED_HORIZONTAL_MARGIN } from '../constants';
import {
  selectAppConfig,
  selectDefaultCover,
  selectDisplayBuyButton,
  selectDisplayContactButton,
  selectDisplayShareButton,
  selectFontFamily,
  selectHasMargin,
  selectRadioTab,
  selectShowHeaderLogo,
  selectSleepTimer,
} from '../features/appconfig/store/selectors';
import { selectCurrentEpisode } from '../features/currentpodcast/store/selectors';
import { updateCurrentRadio, updateIsHD } from '../features/currentradio/store/actions';
import {
  selectCurrentRadio,
  selectIsHD,
  selectRadioMode,
} from '../features/currentradio/store/selectors';
import { selectCurrentTrack } from '../features/currenttrack/store/selectors';
import { showAlert } from '../features/modal/store/actions';
import { selectAudioIsPlaying } from '../features/web/store/selectors';
import { getFontFamily } from '../helpers/fontMapper';
import { useAdjustedFontSize } from '../hooks/useAdjustedFontSize';
import { useCurrentTrackUpdater } from '../hooks/useCurrentTrackUpdater';
import useOrientation from '../hooks/useOrientation';
import i18n from '../localization/i18n';
import { LeftDrawerNavigatorScreenProps } from '../navigation/LeftDrawerNavigator';

export const PlayerScreen = ({
  navigation,
}: LeftDrawerNavigatorScreenProps<'Player'>) => {
  const dispatch = useAppDispatch();
  const appConfig = useAppSelector(selectAppConfig);
  const radioTab = useAppSelector(selectRadioTab);
  const currentTrack = useAppSelector(selectCurrentTrack);
  const displayContactButton = useAppSelector(selectDisplayContactButton);
  const displayShareButton = useAppSelector(selectDisplayShareButton);
  const displayBuyButton = useAppSelector(selectDisplayBuyButton);
  const defaultCover = useAppSelector(selectDefaultCover);
  const showHeaderLogo = useAppSelector(selectShowHeaderLogo);
  const showSleepTimer = useAppSelector(selectSleepTimer);
  const fontFamily = useAppSelector(selectFontFamily);
  const trackNameFontSize = useAdjustedFontSize(mvs(31.67));
  const trackArtistFontSize = useAdjustedFontSize(mvs(25.33));
  const tabTitleFontSize = useAdjustedFontSize(mvs(25));

  // applies custom margin config
  const hasMargin = useAppSelector(selectHasMargin);
  const sleeptimerIconRightMargin = hasMargin ? mvs(32) : REDUCED_HORIZONTAL_MARGIN;
  const bodyHorizontalMargin = hasMargin ? mvs(16) : REDUCED_HORIZONTAL_MARGIN;

  const orientation = useOrientation();
  const currentRadio = useAppSelector(selectCurrentRadio);
  const isHD = useAppSelector(selectIsHD);
  const [showContactModal, setShowContactModal] = useState(false);

  const isTabletPortrait = orientation === 'PORTRAIT' && isTablet;
  const isTabletLandscape = orientation === 'LANDSCAPE' && isTablet;

  const [isAppStarted, setIsAppStarted] = useState(false);
  const radioMode = useAppSelector(selectRadioMode);
  const currentPodcastEpisode = useAppSelector(selectCurrentEpisode);
  const audioIsPlaying = useAppSelector(selectAudioIsPlaying);

  useCurrentTrackUpdater();

  useFocusEffect(
    useCallback(() => {
      const startInterval = setInterval(() => {
        if (currentRadio.id === -1) {
          dispatch(updateCurrentRadio(radioTab.flux[0]));
        } else {
          const isRadioInConfig = radioTab?.flux.findIndex(function (e) {
            return e.id === currentRadio.id;
          });
          if (isRadioInConfig === -1) {
            dispatch(updateCurrentRadio(radioTab?.flux[0]));
          }
        }
        setIsAppStarted(true);
        clearInterval(startInterval);
      }, 2000);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radioTab]),
  );

  useEffect(() => {
    if (currentRadio.id !== -1) {
      const radio = radioTab?.flux.find(function (e) {
        return e.id === currentRadio.id;
      });
      if (radio) {
        dispatch(updateCurrentRadio(radio));
      }
    }
    if (currentRadio.id === -1) {
      dispatch(updateCurrentRadio(radioTab.flux[0]));
    } else {
      const isRadioInConfig = radioTab?.flux.findIndex(function (e) {
        return e.id === currentRadio.id;
      });
      if (isRadioInConfig === -1) {
        dispatch(updateCurrentRadio(radioTab?.flux[0]));
      }
    }
  }, [currentRadio, isAppStarted, isHD, radioTab, dispatch]); // Ne pas ajouter PlayStream (crée un bug fait relance la radio pour les radios RK SD)

  const toggleLeftDrawer = useCallback(() => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  }, [navigation]);

  const toggleRightDrawer = useCallback(() => {
    navigation.getParent()?.dispatch(DrawerActions.toggleDrawer());
  }, [navigation]);

  const tabletCoverImageStyle = {
    width: isTabletPortrait ? mvs(380) : mvs(200),
    alignSelf: 'center',
  };

  const radioName = useMemo(() => {
    if (currentRadio.logoappfield && showHeaderLogo) {
      return (
        <View style={styles.headerLogoContainer}>
          <LogoImage
            source={{
              uri: getUrlFromImageName(appConfig, currentRadio.logoappfield),
            }}
          />
        </View>
      );
    } else {
      return (
        <View style={styles.titleContainer}>
          <CustomText
            numberOfLines={1}
            style={[
              styles.title,
              { color: radioTab.nomColor, fontSize: tabTitleFontSize },
            ]}
            weight="Bold"
          >
            {currentRadio.nomFlux}
          </CustomText>
        </View>
      );
    }
  }, [appConfig, currentRadio, radioTab, tabTitleFontSize]);

  const switchRadioIcon = useMemo(() => {
    if (radioTab.flux.length <= 1) {
      return null;
    }
    return (
      <CustomIcon
        name="switch_radio"
        color={COLORS.WHITE_80}
        size={isTablet ? mvs(28) : 32}
        onPress={toggleRightDrawer}
        testID="multistream_icon"
        accessibilityLabel="multistream_icon"
      />
    );
  }, [radioTab, toggleRightDrawer]);

  const contactIcon = useMemo(() => {
    if (!displayContactButton) {
      return null;
    }
    return (
      <CustomIcon
        name="contact"
        color={COLORS.WHITE}
        size={24}
        onPress={() =>
          dispatch(
            showAlert({
              key: 'only-mobile',
              type: 'info',
              text: i18n.t('alert.onlyMobile'),
              closable: false,
              autoCloseAfterMs: 2000,
            }),
          )
        }
        containerStyle={styles.bottomIcon}
      />
    );
  }, [displayContactButton, dispatch]);

  const buyIcon = useMemo(() => {
    if (!displayBuyButton || !currentTrack.url) {
      return null;
    }
    return (
      <CustomIcon
        name="shop"
        color={COLORS.WHITE}
        size={24}
        onPress={() =>
          dispatch(
            showAlert({
              key: 'only-mobile',
              type: 'info',
              text: i18n.t('alert.onlyMobile'),
              closable: false,
              autoCloseAfterMs: 2000,
            }),
          )
        }
        containerStyle={styles.bottomIcon}
      />
    );
  }, [currentTrack, displayBuyButton, dispatch]);

  const shareIcon = useMemo(() => {
    if (!displayShareButton) {
      return null;
    }
    return (
      <CustomIcon
        name="share_player"
        color={COLORS.WHITE}
        size={24}
        onPress={() =>
          dispatch(
            showAlert({
              key: 'only-mobile',
              type: 'info',
              text: i18n.t('alert.onlyMobile'),
              closable: false,
              autoCloseAfterMs: 2000,
            }),
          )
        }
        containerStyle={styles.bottomIcon}
      />
    );
  }, [displayShareButton, dispatch]);

  const hdIcon = useMemo(() => {
    if (!currentRadio.urlFluxHD && !currentRadio.urlFluxBD) {
      return null;
    }
    return (
      <CustomIcon
        name={isHD ? 'hd' : 'sd'}
        color={COLORS.WHITE}
        onPress={() => {
          dispatch(updateIsHD(!isHD));
          dispatch(updateCurrentRadio(currentRadio));
        }}
        containerStyle={styles.bottomIcon}
      />
    );
  }, [currentRadio, dispatch, isHD]);

  const voteBlock = useMemo(() => {
    if (
      radioTab?.voteOption !== 'YES' ||
      !currentRadio?.urlFlux ||
      (currentRadio?.urlFlux.indexOf('radioking.com') === -1 &&
        currentRadio?.urlFlux.indexOf('radioking.io') === -1)
    ) {
      return null;
    }
    return <VoteButtons />;
  }, [currentRadio, radioTab]);

  const webAudioPlayer = useMemo(() => {
    return (
      <ReactPlayer
        url={!radioMode ? currentPodcastEpisode?.audio : currentRadio.urlFlux}
        playing={audioIsPlaying}
        width="0"
        height="0"
        style={{ display: 'none' }}
        audioonly="true"
        onError={error => {
          dispatch(
            showAlert({
              key: 'playback-error',
              type: 'warning',
              text: i18n.t('alert.playbackErrorMessage'),
              closable: true,
              autoCloseAfterMs: 5000,
            }),
          );
        }}
      />
    );
  }, [audioIsPlaying, currentRadio, currentPodcastEpisode, radioMode, dispatch]);

  return (
    <ScreenLayout
      contentStyle={styles.content}
      headerStyle={[styles.header, isTabletPortrait && { marginBottom: mvs(8) }]}
      headerContents={
        <>
          <View style={styles.menuIconContainer}>
            <MenuIcon color={COLORS.WHITE} toggleLeftDrawer={toggleLeftDrawer} />
          </View>
          {!isTabletLandscape && radioName}
          <View style={styles.headerControls}>
            {isTablet && showSleepTimer && <SleepTimer />}
            <View style={styles.switchRadioIcon}>{switchRadioIcon}</View>
          </View>
        </>
      }
    >
      {webAudioPlayer}
      <View style={{ marginHorizontal: bodyHorizontalMargin }}>
        <CoverImage
          source={currentTrack.image ? { uri: currentTrack.image } : defaultCover}
          sizeStyle={isTablet && tabletCoverImageStyle}
          coverType={radioTab?.coverType}
        />
        <PlayPauseButton iconSize={mvs(25)} style={styles.playPauseButton} />
      </View>
      <View
        style={[
          styles.trackInfo,
          {
            backgroundColor:
              radioTab?.fondTitrage === 'NO' ? undefined : radioTab?.bottomColor,
          },
          isTabletPortrait
            ? // eslint-disable-next-line react-native/no-inline-styles
              { marginBottom: mvs(30), paddingVertical: mvs(25), flex: 0 }
            : undefined,
        ]}
      >
        <ScrollableText
          style={[
            styles.trackName,
            {
              color: radioTab.titleLabelColor,
              fontFamily: getFontFamily(fontFamily, 'Bold'),
              fontSize: trackNameFontSize,
            },
          ]}
        >
          {currentTrack.title}
        </ScrollableText>
        <ScrollableText
          style={[
            styles.trackArtist,
            {
              color: radioTab?.artistLabelColor,
              fontFamily: getFontFamily(fontFamily, 'Regular'),
              fontSize: trackArtistFontSize,
            },
          ]}
        >
          {currentTrack.artist}
        </ScrollableText>
        {voteBlock}
      </View>

      <GradientBackground
        style={IS_IOS ? styles.gradientBottomBlock : null}
        invert={true}
      >
        <View style={styles.bottomBlock}>
          {contactIcon}
          {shareIcon}
          {buyIcon}
          {hdIcon}
          {!isTablet && showSleepTimer ? (
            <View
              style={[
                styles.sleepTimerMobileContainer,
                { right: sleeptimerIconRightMargin },
              ]}
            >
              <SleepTimer />
            </View>
          ) : null}
        </View>
        <SafeAreaView edges={['bottom']} />
      </GradientBackground>
      <ContactModal visible={showContactModal} onRequestClose={setShowContactModal} />
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    justifyContent: 'space-between',
  },
  header: {
    justifyContent: 'space-between',
  },
  menuIconContainer: {
    flex: isTablet ? 1 : 0,
  },
  headerControls: {
    flex: isTablet ? 1 : 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  switchRadioIcon: {
    marginLeft: isTablet ? 25 : 10,
  },
  headerLogoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 5,
  },
  titleContainer: {
    marginLeft: 20,
    alignItems: isTablet ? 'center' : 'flex-start',
    justifyContent: 'center',
    alignSelf: 'center',
    overflow: 'hidden',
    flex: isTablet ? 0 : 1,
  },
  title: {
    marginHorizontal: 12,
    color: COLORS.WHITE,
    lineHeight: mvs(34),
    width: 150,
  },
  playPauseButton: {
    position: 'absolute',
    bottom: mvs(-26.5),
    alignSelf: 'center',
  },
  trackInfo: {
    flex: 1,
    marginTop: mvs(40),
    marginBottom: isTablet ? mvs(15) : 0,
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: mvs(180),
  },
  trackName: {
    color: COLORS.WHITE,
    lineHeight: mvs(43.19),
    paddingHorizontal: 20,
    maxWidth: isTablet ? undefined : 300,
  },
  trackArtist: {
    marginTop: mvs(4),
    color: COLORS.MANATEE,
    lineHeight: mvs(34.55),
    paddingHorizontal: 20,
    maxWidth: isTablet ? undefined : 300,
  },
  gradientBottomBlock: {
    zIndex: 2,
  },
  bottomBlock: {
    paddingTop: isTablet ? 0 : 15,
    paddingBottom: mvs(10),
    paddingHorizontal: mvs(32),
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 45,
    marginBottom: 8,
  },
  bottomIcon: {
    marginHorizontal: mvs(8),
    pointerEvents: 'none',
  },
  sleepTimerMobileContainer: {
    position: 'absolute',
    bottom: mvs(1),
    pointerEvents: 'none',
  },
});
